<template>
    <div class="message-container">
      <!-- 左侧患者列表 -->
      <div class="patient-list">
        <div class="search-container">
          <el-input
            placeholder="搜索患者姓名/病历号"
            v-model="searchQuery"
            prefix-icon="el-icon-search"
            clearable
            @input="filterPatients"
          >
          </el-input>
        </div>
        
        <el-tabs v-model="patientListTab" type="card" @tab-click="handleTabClick">
          <el-tab-pane label="全部患者" name="all"></el-tab-pane>
          <el-tab-pane label="未读留言" name="unread">
            <span slot="label">未读留言 <el-badge :value="getUnreadCount()" class="unread-badge" :hidden="getUnreadCount() === 0"></el-badge></span>
          </el-tab-pane>
        </el-tabs>
        
        <div class="patient-list-content">
          <div 
            v-for="patient in filteredPatients" 
            :key="patient.id"
            :class="['patient-item', selectedPatient && patient.id === selectedPatient.id ? 'active' : '']"
            @click="selectPatient(patient)"
          >
            <div class="avatar-container">
              <el-avatar :src="patient.avatar" :size="40"></el-avatar>
              <el-badge :value="getPatientUnreadCount(patient)" :hidden="getPatientUnreadCount(patient) === 0" class="item-unread"></el-badge>
            </div>
            <div class="patient-info">
              <div class="patient-name">{{ patient.name }}
                <span class="patient-age">{{ patient.age }}岁</span>
              </div>
              <div class="latest-message">{{ getLatestMessage(patient) }}</div>
            </div>
            <div class="message-time">{{ getLatestMessageTime(patient) }}</div>
          </div>
        </div>
      </div>
      
      <!-- 中间消息区域 -->
      <div class="message-area">
        <div v-if="selectedPatient" class="message-header">
          <div class="patient-title">
            <span class="name">{{ selectedPatient.name }}</span>
            <span class="record-number">病历号: {{ selectedPatient.recordNum }}</span>
            <el-tag size="small" :type="selectedPatient.status === '复诊中' ? 'success' : 'warning'">
              {{ selectedPatient.status }}
            </el-tag>
          </div>
          <div class="header-actions">
            <el-button type="text" icon="el-icon-phone-outline" @click="callPatient">电话随访</el-button>
            <el-button type="text" icon="el-icon-document" @click="viewMedicalRecord">查看病历</el-button>
          </div>
        </div>
  
        <div v-if="selectedPatient" class="message-content">
          <div v-for="(message, index) in selectedPatient.messages" :key="index" class="message-block">
            <div class="message-date-divider" v-if="showDateDivider(message, index)">
              <span>{{ formatFullDate(message.timestamp) }}</span>
            </div>
            
            <div :class="['message-wrapper', message.fromPatient ? 'patient-message' : 'doctor-message']">
              <div class="avatar">
                <el-avatar :size="36" :src="message.fromPatient ? selectedPatient.avatar : currentDoctor.avatar"></el-avatar>
              </div>
              <div class="message-bubble">
                <div class="message-info">
                  <span class="sender-name">{{ message.fromPatient ? selectedPatient.name : message.sender }}</span>
                  <span class="sender-role">{{ message.fromPatient ? '患者' : message.role }}</span>
                  <span class="message-time">{{ formatTime(message.timestamp) }}</span>
                </div>
                
                <div class="message-text" v-html="message.content"></div>
                
                <div v-if="message.images && message.images.length > 0" class="message-images">
                  <el-image 
                    v-for="(img, imgIndex) in message.images" 
                    :key="imgIndex"
                    :src="img"
                    :preview-src-list="message.images"
                    fit="cover"
                    class="message-image"
                  ></el-image>
                </div>
                
                <div v-if="message.attachments && message.attachments.length > 0" class="message-attachments">
                  <div v-for="(attachment, attachmentIndex) in message.attachments" :key="attachmentIndex" class="message-attachment">
                    <i class="el-icon-document"></i>
                    <span>{{ attachment.name }}</span>
                    <el-button type="text" icon="el-icon-download" @click="downloadAttachment(attachment)">下载</el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <div v-if="selectedPatient" class="message-reply">
          <div class="reply-toolbar">
            <el-button type="text" icon="el-icon-picture" @click="handleUploadImage">添加图片</el-button>
            <el-button type="text" icon="el-icon-paperclip" @click="handleUploadFile">添加附件</el-button>
            <el-dropdown @command="handleTemplateCommand" trigger="click">
              <el-button type="text">
                快捷模板<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-for="(template, index) in replyTemplates" :key="index" :command="index">
                  {{ template.title }}
                </el-dropdown-item>
                <el-dropdown-item divided command="manage">管理模板</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
          
          <el-input
            type="textarea"
            :rows="3"
            placeholder="请输入回复内容..."
            v-model="replyContent"
            resize="none"
          ></el-input>
          
          <div class="upload-preview" v-if="uploadFiles.length > 0">
            <div v-for="(file, index) in uploadFiles" :key="index" class="upload-file">
              <span>{{ file.name }}</span>
              <el-button type="text" icon="el-icon-delete" @click="removeUploadFile(index)"></el-button>
            </div>
          </div>
          
          <div class="reply-actions">
            <el-button type="primary" @click="sendReply" :disabled="!replyContent && uploadFiles.length === 0">
              发送回复
            </el-button>
          </div>
        </div>
        
        <div v-if="!selectedPatient" class="no-patient-selected">
          <i class="el-icon-message"></i>
          <p>请选择一个患者开始对话</p>
        </div>
      </div>
      
      <!-- 右侧患者信息 -->
      <div class="patient-detail" v-if="selectedPatient">
        <div class="patient-profile">
          <div class="profile-header">
            <el-avatar :src="selectedPatient.avatar" :size="80"></el-avatar>
            <h3>{{ selectedPatient.name }}</h3>
            <div class="basic-info">
              <p>{{ selectedPatient.gender === 'male' ? '男' : '女' }} {{ selectedPatient.age }}岁</p>
              <p>{{ selectedPatient.diagnosis }}</p>
            </div>
          </div>
          
          <el-divider content-position="left">患者信息</el-divider>
          
          <el-descriptions :column="1" border>
            <el-descriptions-item label="病历号">{{ selectedPatient.recordNum }}</el-descriptions-item>
            <el-descriptions-item label="联系电话">{{ selectedPatient.phone }}</el-descriptions-item>
            <el-descriptions-item label="随访状态">
              <el-tag :type="getStatusType(selectedPatient.status)">{{ selectedPatient.status }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="下次随访">{{ selectedPatient.nextFollowUp }}</el-descriptions-item>
            <el-descriptions-item label="主治医师">{{ selectedPatient.doctor }}</el-descriptions-item>
          </el-descriptions>
          
          <el-divider content-position="left">快捷操作</el-divider>
          
          <div class="quick-actions">
            <el-button type="primary" plain icon="el-icon-document" @click="sendQuestionnaire">发送随访问卷</el-button>
            <el-button type="success" plain icon="el-icon-date" @click="createAppointment">创建预约</el-button>
            <el-button type="warning" plain icon="el-icon-folder" @click="viewReports">查看检查报告</el-button>
            <el-button type="info" plain icon="el-icon-data-analysis" @click="viewDataTrend">数据趋势</el-button>
          </div>
          
          <el-divider content-position="left">最近随访记录</el-divider>
          
          <div class="follow-up-records">
            <div v-for="(record, index) in selectedPatient.followUpRecords" :key="index" class="record-item">
              <div class="record-time">{{ formatDate(record.date) }}</div>
              <div class="record-content">
                <p><strong>{{ record.type }}</strong> - {{ record.doctor }}</p>
                <p class="record-summary">{{ record.summary }}</p>
                <el-button type="text" @click="viewFollowUpDetail(record)">查看详情</el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 模板管理对话框 -->
      <el-dialog title="快捷回复模板管理" :visible.sync="templateDialogVisible" width="600px">
        <el-table :data="replyTemplates" style="width: 100%">
          <el-table-column prop="title" label="模板名称"></el-table-column>
          <el-table-column label="操作" width="150">
            <template slot-scope="scope">
              <el-button size="mini" type="text" @click="editTemplate(scope.row)">编辑</el-button>
              <el-button size="mini" type="text" @click="deleteTemplate(scope.$index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div style="margin-top: 20px;">
          <el-button type="primary" @click="addNewTemplate">新增模板</el-button>
        </div>
      </el-dialog>
      
      <!-- 编辑模板对话框 -->
      <el-dialog :title="currentTemplateAction === 'add' ? '新增模板' : '编辑模板'" :visible.sync="editTemplateDialogVisible" width="500px">
        <el-form :model="currentTemplate" label-width="80px">
          <el-form-item label="模板名称">
            <el-input v-model="currentTemplate.title"></el-input>
          </el-form-item>
          <el-form-item label="模板内容">
            <el-input type="textarea" :rows="5" v-model="currentTemplate.content"></el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="editTemplateDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="saveTemplate">确 定</el-button>
        </div>
      </el-dialog>
    </div>
  </template>
  
  <script>
  export default {
    name: "DoctorMessage",
    data() {
      return {
        // 当前医生信息
        currentDoctor: {
          id: "D001",
          name: "张医生",
          avatar: "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png",
          department: "心内科",
          title: "主任医师"
        },
        
        // 搜索查询
        searchQuery: "",
        patientListTab: "all",
        
        // 选中的患者
        selectedPatient: null,
        
        // 回复内容
        replyContent: "",
        uploadFiles: [],
        
        // 对话框
        templateDialogVisible: false,
        editTemplateDialogVisible: false,
        currentTemplateAction: "add",
        currentTemplateIndex: -1,
        currentTemplate: {
          title: "",
          content: ""
        },
        
        // 快捷回复模板
        replyTemplates: [
          { 
            title: "常规随访提醒", 
            content: "您好，根据您的随访计划，建议您在下周进行血压监测并记录。如有任何不适，请及时反馈。" 
          },
          { 
            title: "用药指导", 
            content: "请按照医嘱服药，注意药物需在饭后半小时服用，如有不良反应请立即联系我。" 
          },
          {
            title: "复诊通知",
            content: "您好，请于下周三上午9点来医院复诊，记得带上您的检查报告和近期的用药记录。"
          },
          {
            title: "检查结果正常",
            content: "您的最新检查结果已出，各项指标均在正常范围内，请继续保持良好的生活习惯。"
          },
          {
            title: "生活建议",
            content: "建议您保持规律作息，适当运动，饮食清淡，避免过度劳累和情绪波动。"
          }
        ],
        
        // 患者列表数据
        patients: [
          {
            id: "P001",
            name: "许昊然",
            age: 65,
            gender: "male",
            recordNum: "HF20245001",
            phone: "13812345678",
            status: "复诊中",
            diagnosis: "冠心病，高血压",
            doctor: "王主任",
            nextFollowUp: "2024-06-10",
            avatar: "https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png",
            unreadCount: 2,
            followUpRecords: [
              {
                date: "2024-05-12",
                type: "电话随访",
                doctor: "李医生",
                summary: "患者血压稳定，无明显不适症状",
                detail: "详细内容....."
              },
              {
                date: "2024-04-28",
                type: "门诊随访",
                doctor: "王主任",
                summary: "复查心电图，窦性心律，未见明显ST-T改变",
                detail: "详细内容....."
              }
            ],
            messages: [
              {
                fromPatient: true,
                sender: "许昊然",
                content: "医生，我的血压最近有点波动，早上有时候会感到头晕。",
                timestamp: new Date(2024, 4, 15, 9, 23),
                read: true
              },
              {
                fromPatient: false,
                sender: "王医生",
                role: "主治医师",
                content: "您好张先生，请问您是什么时间段测量的血压？有没有具体的数值？头晕的症状持续多久？",
                timestamp: new Date(2024, 4, 15, 10, 5),
                read: true
              },
              {
                fromPatient: true,
                sender: "许昊然",
                content: "早上七点左右测的，血压大概是150/95，头晕一般持续十几分钟就好了。",
                timestamp: new Date(2024, 4, 15, 10, 18),
                read: true,
                images: ["https://shadow.elemecdn.com/app/element/hamburger.9cf7b091-55e9-11e9-a976-7f4d0b07eef6.png"]
              },
              {
                fromPatient: false,
                sender: "王医生",
                role: "主治医师",
                content: "您的血压确实偏高，建议您：<br>1. 继续按时服用降压药物<br>2. 减少盐的摄入量<br>3. 每天测量并记录血压，最好早晚各一次<br>4. 适当增加有氧运动<br><br>如果头晕症状加重或频繁出现，请及时就诊。",
                timestamp: new Date(2024, 4, 15, 10, 30),
                read: true
              },
              {
                fromPatient: true,
                sender: "许昊然",
                content: "好的，我会按照您的建议做。另外，我这两天有点咳嗽，需要吃点什么药吗？",
                timestamp: new Date(2024, 5, 18, 8, 45),
                read: false
              },
              {
                fromPatient: true,
                sender: "许昊然",
                content: "这是我最近的血压记录表",
                timestamp: new Date(2024, 5, 18, 8, 46),
                read: false,
                attachments: [
                  {
                    name: "血压记录表.xlsx",
                    url: "#",
                    size: "15KB"
                  }
                ]
              }
            ]
          },
          {
            id: "P002",
            name: "苏雅婷",
            age: 58,
            gender: "male",
            recordNum: "HF20245002",
            phone: "13987654321",
            status: "随访中",
            diagnosis: "2型糖尿病",
            doctor: "张医生",
            nextFollowUp: "2024-06-15",
            avatar: "https://cube.elemecdn.com/9/c2/f0ee8a3c7c9638a54940382568c9cpng.png",
            unreadCount: 0,
            followUpRecords: [
              {
                date: "2024-05-10",
                type: "线上随访",
                doctor: "张医生",
                summary: "患者血糖控制良好，饮食规律",
                detail: "详细内容....."
              }
            ],
            messages: [
              {
                fromPatient: true,
                sender: "苏雅婷",
                content: "医生，我想咨询一下我的降糖药是不是需要调整剂量？",
                timestamp: new Date(2024, 4, 10, 15, 20),
                read: true
              },
              {
                fromPatient: false,
                sender: "张医生",
                role: "主治医师",
                content: "李先生，需要根据您最近的血糖监测结果来判断。请问您最近的空腹和餐后血糖值是多少？药物按时服用了吗？",
                timestamp: new Date(2024, 4, 10, 16, 5),
                read: true
              },
              {
                fromPatient: true,
                sender: "苏雅婷",
                content: "早上空腹一般在6.0-7.0之间，餐后两小时大概9.0左右。药一直按时吃的。",
                timestamp: new Date(2024, 4, 10, 16, 15),
                read: true
              },
              {
                fromPatient: false,
                sender: "张医生",
                role: "主治医师",
                content: "您目前的血糖控制情况还可以，暂时不需要调整药物剂量。请继续保持规律作息和饮食控制，每周至少监测3次血糖并记录。下次随访时带上您的血糖记录本。",
                timestamp: new Date(2024, 4, 10, 16, 25),
                read: true
              }
            ]
          },
          {
            id: "P003",
            name: "林书辰",
            age: 72,
            gender: "male",
            recordNum: "HF20245003",
            phone: "13712345678",
            status: "术后随访",
            diagnosis: "肺癌术后",
            doctor: "赵医生",
            nextFollowUp: "2024-06-05",
            avatar: "https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png",
            unreadCount: 3,
            followUpRecords: [
              {
                date: "2024-05-15",
                type: "门诊随访",
                doctor: "赵医生",
                summary: "术后恢复良好，建议定期复查",
                detail: "详细内容....."
              }
            ],
            messages: [
              {
                fromPatient: true,
                sender: "林书辰",
                content: "医生，我术后伤口有点痒，这个正常吗？",
                timestamp: new Date(2024, 5, 17, 9, 10),
                read: false
              },
              {
                fromPatient: true,
                sender: "林书辰",
                content: "这是我伤口的照片",
                timestamp: new Date(2024, 5, 17, 9, 11),
                read: false,
                images: ["https://shadow.elemecdn.com/a/17/d43b41abc8c413096227590fb94aejpeg.jpeg"]
              },
              {
                fromPatient: true,
                sender: "林书辰",
                content: "我最近咳嗽有些加重，需要去医院吗？",
                timestamp: new Date(2024, 5, 17, 14, 25),
                read: false
              }
            ]
          },
          {
            id: "P004",
            name: "程若曦",
            age: 45,
            gender: "female",
            recordNum: "HF20245004",
            phone: "13612345678",
            status: "随访中",
            diagnosis: "甲状腺功能亢进",
            doctor: "张医生",
            nextFollowUp: "2024-06-20",
            avatar: "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png",
            unreadCount: 0,
            followUpRecords: [
              {
                date: "2024-05-05",
                type: "电话随访",
                doctor: "张医生",
                summary: "症状改善，心率有所下降",
                detail: "详细内容....."
              }
            ],
            messages: [
              {
                fromPatient: true,
                sender: "程若曦",
                content: "医生，我按照您的建议调整了饮食，感觉好多了！",
                timestamp: new Date(2024, 5, 5, 10, 30),
                read: true
              },
              {
                fromPatient: false,
                sender: "张医生",
                role: "主治医师",
                content: "赵女士，很高兴听到您的症状有所改善！请问您最近有测量心率吗？有没有感到心慌、出汗等症状？",
                timestamp: new Date(2024, 5, 5, 11, 15),
                read: true
              },
              {
                fromPatient: true,
                sender: "程若曦",
                content: "心率现在基本在80-90之间，偶尔会有点心慌，但比之前好多了。出汗的情况也少了。",
                timestamp: new Date(2024, 5, 5, 11, 30),
                read: true
              }
            ]
          },
          {
            id: "P005",
            name: "郭子轩",
            age: 68,
            gender: "male",
            recordNum: "HF20245005",
            phone: "13512345678",
            status: "随访结束",
            diagnosis: "前列腺增生",
            doctor: "张医生",
            nextFollowUp: "已结束",
            avatar: "https://cube.elemecdn.com/9/c2/f0ee8a3c7c9638a54940382568c9cpng.png",
            unreadCount: 1,
            followUpRecords: [
              {
                date: "2024-04-20",
                type: "门诊随访",
                doctor: "张医生",
                summary: "症状改善明显，随访计划已完成",
                detail: "详细内容....."
              }
            ],
            messages: [
              {
                fromPatient: false,
                sender: "张医生",
                role: "主治医师",
                content: "钱先生您好，您的随访计划已经完成，根据评估您的恢复情况良好。如有任何不适，请随时联系我们。",
                timestamp: new Date(2024, 4, 20, 14, 0),
                read: true
              },
              {
                fromPatient: true,
                sender: "郭子轩",
                content: "谢谢医生，我现在感觉很好，不过我想问问以后需要定期检查吗？",
                timestamp: new Date(2024, 5, 18, 9, 30),
                read: false
              }
            ]
          }
        ]
      };
    },
    computed: {
      filteredPatients() {
        let result = this.patients;
        
        // 标签筛选
        if (this.patientListTab === "unread") {
          result = result.filter(patient => this.getPatientUnreadCount(patient) > 0);
        }
        
        // 关键词搜索
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          result = result.filter(patient => 
            patient.name.toLowerCase().includes(query) || 
            patient.recordNum.toLowerCase().includes(query)
          );
        }
        
        return result;
      }
    },
    methods: {
      // 患者列表相关方法
      filterPatients() {
        // 搜索过滤逻辑在computed中实现
      },
      
      handleTabClick() {
        // 切换标签逻辑在computed中实现
      },
      
      selectPatient(patient) {
        this.selectedPatient = patient;
        // 标记该患者的所有消息为已读
        if (patient && patient.messages) {
          patient.messages.forEach(msg => {
            if (msg.fromPatient) {
              msg.read = true;
            }
          });
        }
      },
      
      getUnreadCount() {
        return this.patients.reduce((count, patient) => count + this.getPatientUnreadCount(patient), 0);
      },
      
      getPatientUnreadCount(patient) {
        return patient.messages.filter(msg => msg.fromPatient && !msg.read).length;
      },
      
      getLatestMessage(patient) {
        if (patient.messages && patient.messages.length > 0) {
          const lastMsg = patient.messages[patient.messages.length - 1];
          // 去掉HTML标签，截取内容
          const content = lastMsg.content.replace(/<[^>]*>/g, '');
          return content.length > 20 ? content.substring(0, 20) + '...' : content;
        }
        return '无消息记录';
      },
      
      getLatestMessageTime(patient) {
        if (patient.messages && patient.messages.length > 0) {
          const lastMsg = patient.messages[patient.messages.length - 1];
          return this.formatTime(lastMsg.timestamp);
        }
        return '';
      },
      
      // 消息区域相关方法
      showDateDivider(message, index) {
        if (index === 0) return true;
        
        const prevMsg = this.selectedPatient.messages[index - 1];
        const prevDate = new Date(prevMsg.timestamp);
        const currDate = new Date(message.timestamp);
        
        return prevDate.toDateString() !== currDate.toDateString();
      },
      
      formatFullDate(date) {
        if (!(date instanceof Date)) {
          date = new Date(date);
        }
        
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === today.toDateString()) {
          return '今天';
        } else if (date.toDateString() === yesterday.toDateString()) {
          return '昨天';
        } else {
          return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
        }
      },
      
      formatTime(date) {
        if (!(date instanceof Date)) {
          date = new Date(date);
        }
        
        const now = new Date();
        const diff = Math.floor((now - date) / 1000); // 秒差
        
        if (diff < 60) {
          return '刚刚';
        } else if (diff < 3600) {
          return Math.floor(diff / 60) + '分钟前';
        } else if (diff < 86400 && date.getDate() === now.getDate()) {
          return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
        } else if (diff < 172800 && date.getDate() === now.getDate() - 1) {
          return `昨天 ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
        } else {
          return `${date.getMonth() + 1}月${date.getDate()}日`;
        }
      },
      
      formatDate(dateStr) {
        const date = new Date(dateStr);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      },
      
      // 回复相关方法
      handleUploadImage() {
        this.$message.info('此处应打开图片上传对话框');
        // 模拟添加一个图片文件
        this.uploadFiles.push({
          name: '图片.jpg',
          type: 'image',
          url: 'data:image/jpeg;base64,/9j/4AAQ...'
        });
      },
      
      handleUploadFile() {
        this.$message.info('此处应打开文件上传对话框');
        // 模拟添加一个文件
        this.uploadFiles.push({
          name: '患者指导说明.pdf',
          type: 'file',
          size: '245KB'
        });
      },
      
      removeUploadFile(index) {
        this.uploadFiles.splice(index, 1);
      },
      
      handleTemplateCommand(command) {
        if (command === 'manage') {
          this.templateDialogVisible = true;
        } else {
          // 插入模板内容
          this.replyContent = this.replyTemplates[command].content;
        }
      },
      
      sendReply() {
        if (!this.selectedPatient) return;
        
        // 构建新消息对象
        const newMessage = {
          fromPatient: false,
          sender: this.currentDoctor.name,
          role: this.currentDoctor.title,
          content: this.replyContent || '已收到附件',
          timestamp: new Date(),
          read: true
        };
        
        // 如果有上传文件，添加到消息中
        if (this.uploadFiles.length > 0) {
          const attachments = this.uploadFiles.filter(file => file.type === 'file').map(file => ({
            name: file.name,
            url: file.url || '#',
            size: file.size || '0KB'
          }));
          
          if (attachments.length > 0) {
            newMessage.attachments = attachments;
          }
          
          const images = this.uploadFiles.filter(file => file.type === 'image').map(file => file.url);
          if (images.length > 0) {
            newMessage.images = images;
          }
        }
        
        // 添加到消息列表
        this.selectedPatient.messages.push(newMessage);
        
        // 清空回复框
        this.replyContent = '';
        this.uploadFiles = [];
        
        // 提示发送成功
        this.$message.success('回复已发送');
      },
      
      downloadAttachment(attachment) {
        this.$message.info(`正在下载: ${attachment.name}`);
        // 实际项目中这里应该是真实下载逻辑
      },
      
      // 模板管理相关方法
      addNewTemplate() {
        this.currentTemplateAction = 'add';
        this.currentTemplateIndex = -1;
        this.currentTemplate = {
          title: '',
          content: ''
        };
        this.editTemplateDialogVisible = true;
      },
      
      editTemplate(template) {
        this.currentTemplateAction = 'edit';
        this.currentTemplateIndex = this.replyTemplates.findIndex(t => t.title === template.title);
        this.currentTemplate = {...template};
        this.editTemplateDialogVisible = true;
      },
      
      deleteTemplate(index) {
        this.$confirm('确定要删除此模板吗?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.replyTemplates.splice(index, 1);
          this.$message.success('删除成功');
        }).catch(() => {});
      },
      
      saveTemplate() {
        if (!this.currentTemplate.title || !this.currentTemplate.content) {
          this.$message.warning('模板名称和内容不能为空');
          return;
        }
        
        if (this.currentTemplateAction === 'add') {
          this.replyTemplates.push({...this.currentTemplate});
          this.$message.success('新增模板成功');
        } else {
          this.replyTemplates[this.currentTemplateIndex] = {...this.currentTemplate};
          this.$message.success('更新模板成功');
        }
        
        this.editTemplateDialogVisible = false;
      },
      
      // 患者操作相关方法
      getStatusType(status) {
        switch(status) {
          case '复诊中':
            return 'success';
          case '随访中':
            return 'primary';
          case '术后随访':
            return 'warning';
          case '随访结束':
            return 'info';
          default:
            return '';
        }
      },
      
      callPatient() {
        this.$message.info(`正在拨打患者${this.selectedPatient.name}的电话: ${this.selectedPatient.phone}`);
      },
      
      viewMedicalRecord() {
        this.$message.info(`查看${this.selectedPatient.name}的电子病历`);
      },
      
      sendQuestionnaire() {
        this.$message.success(`已向${this.selectedPatient.name}发送随访问卷`);
      },
      
      createAppointment() {
        this.$message.info(`正在为${this.selectedPatient.name}创建预约`);
      },
      
      viewReports() {
        this.$message.info(`查看${this.selectedPatient.name}的检查报告`);
      },
      
      viewDataTrend() {
        this.$message.info(`查看${this.selectedPatient.name}的数据趋势`);
      },
      
      viewFollowUpDetail(record) {
        this.$message.info(`查看${this.selectedPatient.name}在${record.date}的${record.type}详情`);
      }
    }
  }
  </script>
  
  <style scoped>
  .message-container {
    display: flex;
    height: calc(100vh - 120px);
    background-color: #f5f7fa;
  }
  
  /* 左侧患者列表样式 */
  .patient-list {
    width: 280px;
    border-right: 1px solid #ebeef5;
    display: flex;
    flex-direction: column;
    background-color: #fff;
  }
  
  .search-container {
    padding: 15px;
  }
  
  .patient-list-content {
    flex: 1;
    overflow-y: auto;
  }
  
  .patient-item {
    padding: 12px 15px;
    cursor: pointer;
    display: flex;
    border-bottom: 1px solid #f2f2f2;
    position: relative;
  }
  
  .patient-item:hover {
    background-color: #f5f7fa;
  }
  
  .patient-item.active {
    background-color: #ecf5ff;
  }
  
  .avatar-container {
    position: relative;
    margin-right: 12px;
  }
  
  .item-unread {
    position: absolute;
    top: -5px;
    right: -5px;
  }
  
  .patient-info {
    flex: 1;
    overflow: hidden;
  }
  
  .patient-name {
    font-weight: bold;
    color: #303133;
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
  }
  
  .patient-age {
    font-weight: normal;
    font-size: 12px;
    color: #909399;
  }
  
  .latest-message {
    font-size: 13px;
    color: #606266;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  
  .message-time {
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
  }
  
  .unread-badge {
    margin-top: -5px;
  }
  
  /* 中间消息区域样式 */
  .message-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: #f5f7fa;
  }
  
  .message-header {
    padding: 15px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .patient-title {
    display: flex;
    align-items: center;
  }
  
  .patient-title .name {
    font-size: 16px;
    font-weight: bold;
    margin-right: 10px;
  }
  
  .patient-title .record-number {
    font-size: 14px;
    color: #606266;
    margin-right: 10px;
  }
  
  .message-content {
    flex: 1;
    padding: 15px;
    overflow-y: auto;
  }
  
  .message-date-divider {
    text-align: center;
    margin: 15px 0;
  }
  
  .message-date-divider span {
    background-color: #f2f6fc;
    padding: 5px 15px;
    border-radius: 15px;
    font-size: 12px;
    color: #909399;
  }
  
  .message-wrapper {
    display: flex;
    margin-bottom: 20px;
  }
  
  .patient-message {
    flex-direction: row;
  }
  
  .doctor-message {
    flex-direction: row-reverse;
  }
  
  .avatar {
    margin: 0 10px;
  }
  
  .message-bubble {
    max-width: 70%;
    border-radius: 8px;
    padding: 12px;
    position: relative;
  }
  
  .patient-message .message-bubble {
    background-color: #fff;
    border: 1px solid #ebeef5;
  }
  
  .doctor-message .message-bubble {
    background-color: #ecf5ff;
    border: 1px solid #d9ecff;
  }
  
  .message-info {
    margin-bottom: 5px;
  }
  
  .sender-name {
    font-weight: bold;
    color: #303133;
  }
  
  .sender-role {
    font-size: 12px;
    color: #909399;
    margin-left: 5px;
  }
  
  .message-time {
    font-size: 12px;
    color: #909399;
    float: right;
  }
  
  .message-text {
    font-size: 14px;
    color: #606266;
    line-height: 1.5;
    word-break: break-word;
  }
  
  .message-images {
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
  }
  
  .message-image {
    width: 150px;
    height: 150px;
    margin-right: 10px;
    margin-bottom: 10px;
    border-radius: 4px;
    object-fit: cover;
  }
  
  .message-attachments {
    margin-top: 10px;
  }
  
  .message-attachment {
    background-color: #f5f7fa;
    padding: 8px 12px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }
  
  .message-attachment i {
    margin-right: 5px;
    color: #409eff;
  }
  
  .message-attachment span {
    flex: 1;
    font-size: 13px;
  }
  
  .message-reply {
    background-color: #fff;
    padding: 15px;
    border-top: 1px solid #ebeef5;
  }
  
  .reply-toolbar {
    margin-bottom: 10px;
    display: flex;
  }
  
  .upload-preview {
    margin: 10px 0;
  }
  
  .upload-file {
    background-color: #f5f7fa;
    padding: 5px 10px;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 5px;
  }
  
  .reply-actions {
    margin-top: 10px;
    text-align: right;
  }
  
  .no-patient-selected {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #909399;
  }
  
  .no-patient-selected i {
    font-size: 60px;
    margin-bottom: 15px;
  }
  
  /* 右侧患者详情样式 */
  .patient-detail {
    width: 300px;
    background-color: #fff;
    border-left: 1px solid #ebeef5;
    overflow-y: auto;
  }
  
  .patient-profile {
    padding: 20px;
  }
  
  .profile-header {
    text-align: center;
    margin-bottom: 20px;
  }
  
  .profile-header h3 {
    margin: 10px 0 5px;
    font-size: 18px;
    color: #303133;
  }
  
  .basic-info {
    color: #606266;
    font-size: 14px;
  }
  
  .basic-info p {
    margin: 5px 0;
  }
  
  .quick-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;
  }
  
  .quick-actions .el-button {
    width: calc(50% - 5px);
    margin-left: 0 !important;
  }
  
  .follow-up-records {
    margin-top: 15px;
  }
  
  .record-item {
    padding: 10px 0;
    border-bottom: 1px solid #ebeef5;
  }
  
  .record-time {
    font-size: 12px;
    color: #909399;
    margin-bottom: 5px;
  }
  
  .record-content {
    font-size: 14px;
  }
  
  .record-summary {
    color: #606266;
    margin: 5px 0;
  }
  
  /* 覆盖Element UI样式 */
  .el-tabs--card {
    height: 40px;
  }
  
  :deep(.el-tabs__header) {
    margin-bottom: 0;
  }
  
  :deep(.el-tabs__nav) {
    border: none !important;
  }
  
  :deep(.el-descriptions-item__label) {
    width: 80px;
  }
  </style>